import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { CircleNotch } from "@phosphor-icons/react";
import QuizQuestion from "./QuizQuestion";
import QuizSummary from "./QuizSummary";
import {
  quizApi,
  type AnswerResultDto,
  type QuizSessionDto,
  type QuizSummaryDto,
} from "./quizApi";

export default function ModuleQuizMain() {
  const { moduleId } = useParams();
  const modId = Number(moduleId);

  const [session, setSession] = useState<QuizSessionDto | null>(null);
  const [index, setIndex] = useState(0);
  const [result, setResult] = useState<AnswerResultDto | null>(null);
  const [summary, setSummary] = useState<QuizSummaryDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setSummary(null);
    setResult(null);
    try {
      const s = await quizApi.start(modId);
      setSession(s);
      // Yarim kalmis oturumda cevaplanmamis ilk soruya atla
      const firstUnanswered = s.questions.findIndex((q) => !s.answered.includes(q.id));
      setIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Pratik yüklenemedi.");
    }
  }, [modId]);

  useEffect(() => {
    if (Number.isFinite(modId)) load();
  }, [modId, load]);

  const handleSubmit = async (
    answer: string,
    clientTestResult?: { passed: number; total: number; kind?: "structural" | "assertion" },
  ) => {
    if (!session) return;
    setSubmitting(true);
    try {
      const r = await quizApi.answer(session.sessionId, {
        questionId: session.questions[index].id,
        answer,
        clientTestResult,
      });
      setResult(r);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Cevap gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (!session) return;
    setResult(null);
    if (index + 1 < session.questions.length) {
      setIndex(index + 1);
      return;
    }
    const s = await quizApi.finish(session.sessionId);
    setSummary(s);
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center text-white/70">
        <p>{error}</p>
      </div>
    );
  }

  if (summary) return <QuizSummary summary={summary} onRestart={load} />;

  if (!session) {
    return (
      <div className="flex justify-center py-20 text-white/60">
        <CircleNotch size={28} className="animate-spin" />
      </div>
    );
  }

  const progress = (index / session.questions.length) * 100;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 flex flex-col gap-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-white/90">Modül Sonu Pratik</h1>
        </div>
        <p className="text-xs text-white/50 mt-1">
          Sorular bu modülden ve daha önce öğrendiğin ilgili konulardan karışık geliyor.
        </p>
        <p className="text-xs text-myAmber/80 mt-1">
          Bu bölüm deneme aşamasında. Bir soruda ya da puanlamada tuhaflık görürsen sağ üstteki soru numarasıyla eğitmenine haber ver.
        </p>
        <div className="h-1 rounded-full bg-white/10 mt-4 overflow-hidden">
          <div
            className="h-full bg-myGreen rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <QuizQuestion
        key={session.questions[index].id}
        question={session.questions[index]}
        index={index}
        total={session.questions.length}
        result={result}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
        onNext={handleNext}
      />
    </div>
  );
}
